import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Plus, Trash2, Loader2, Zap } from 'lucide-react';

const TRIGGER_OPTIONS = [
  { value: 'registrations.welcome', label: 'Willkommens-Mail (nach Genehmigung)' },
  { value: 'registrations.confirm', label: 'Double-Opt-In Bestätigung' },
  { value: 'registrations.admin_notify', label: 'Admin-Benachrichtigung (neue Anmeldung)' },
  { value: 'registrations.slot_confirmation', label: 'Termin-Bestätigung (Eltern/Lehrkraft)' },
];

const triggerLabel = (value) =>
  TRIGGER_OPTIONS.find((o) => o.value === value)?.label || value;

export const AutoApproveRulesDialog = ({ open, onOpenChange }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ name: '', subject_contains: '', recipient_pattern: '', trigger_source: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/email-queue/auto-approve-rules');
      setRules(r.data || []);
    } catch (e) {
      toast.error('Regeln konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const create = async () => {
    if (!draft.name.trim()) { toast.error('Bitte einen Regelnamen angeben'); return; }
    if (!draft.subject_contains.trim() && !draft.recipient_pattern.trim() && !draft.trigger_source) {
      toast.error('Mindestens ein Kriterium (E-Mail-Typ / Betreff / Empfänger) angeben');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/email-queue/auto-approve-rules', {
        name: draft.name.trim(),
        subject_contains: draft.subject_contains.trim() || null,
        recipient_pattern: draft.recipient_pattern.trim() || null,
        trigger_source: draft.trigger_source || null,
        enabled: true,
      });
      toast.success('Regel angelegt');
      setDraft({ name: '', subject_contains: '', recipient_pattern: '', trigger_source: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (rule, enabled) => {
    try {
      await api.put(`/admin/email-queue/auto-approve-rules/${rule.id}`, { enabled });
      setRules((p) => p.map((r) => (r.id === rule.id ? { ...r, enabled } : r)));
    } catch (e) {
      toast.error('Umschalten fehlgeschlagen');
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Regel „${rule.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/email-queue/auto-approve-rules/${rule.id}`);
      toast.success('Regel gelöscht');
      setRules((p) => p.filter((r) => r.id !== rule.id));
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="auto-approve-rules-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-brand-red" /> Auto-Freigabe-Regeln
          </DialogTitle>
          <DialogDescription>
            E-Mails, die auf eine aktive Regel passen, werden automatisch freigegeben und sofort versendet —
            ohne manuelle Freigabe. Gilt nur für den aktuell gewählten Mandanten.
          </DialogDescription>
        </DialogHeader>

        {/* Neue Regel */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Neue Regel</div>
          <div>
            <Label className="text-xs text-slate-500">Name der Regel</Label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
              placeholder='z. B. "Double-Opt-In Bestätigungen"'
              data-testid="auto-approve-rule-name"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">E-Mail-Typ (optional)</Label>
            <select
              value={draft.trigger_source}
              onChange={(e) => setDraft((p) => ({ ...p, trigger_source: e.target.value }))}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="auto-approve-rule-trigger"
            >
              <option value="">— alle E-Mail-Typen —</option>
              {TRIGGER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Betreff enthält (optional)</Label>
              <Input
                value={draft.subject_contains}
                onChange={(e) => setDraft((p) => ({ ...p, subject_contains: e.target.value }))}
                placeholder="z. B. Bitte bestätige deine Anmeldung"
                data-testid="auto-approve-rule-subject"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Empfänger (optional)</Label>
              <Input
                value={draft.recipient_pattern}
                onChange={(e) => setDraft((p) => ({ ...p, recipient_pattern: e.target.value }))}
                placeholder="exakte E-Mail oder @domain.de"
                data-testid="auto-approve-rule-recipient"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Mindestens ein Kriterium erforderlich. Sind mehrere gesetzt, müssen alle zutreffen.
          </p>
          <div className="flex justify-end">
            <Button onClick={create} disabled={saving} data-testid="auto-approve-rule-create-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Regel anlegen
            </Button>
          </div>
        </div>

        {/* Bestehende Regeln */}
        <div className="mt-2">
          <div className="text-sm font-semibold text-slate-800 mb-2">Aktive Regeln</div>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Laden …
            </div>
          ) : rules.length === 0 ? (
            <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg" data-testid="auto-approve-rules-empty">
              Noch keine Regeln — alle Mails erfordern weiterhin manuelle Freigabe.
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className="flex flex-wrap items-center gap-3 border border-slate-200 rounded-lg p-3"
                  data-testid={`auto-approve-rule-row-${rule.id}`}
                >
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium text-slate-900">{rule.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {[
                        rule.trigger_source && `E-Mail-Typ „${triggerLabel(rule.trigger_source)}“`,
                        rule.subject_contains && `Betreff enthält „${rule.subject_contains}“`,
                        rule.recipient_pattern && `Empfänger ${rule.recipient_pattern}`,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <Badge variant="secondary" className="gap-1" title="Automatisch freigegebene Mails">
                    <Zap className="w-3 h-3 text-brand-red" /> {rule.hits}×
                  </Badge>
                  <Switch
                    checked={!!rule.enabled}
                    onCheckedChange={(v) => toggle(rule, v)}
                    data-testid={`auto-approve-rule-toggle-${rule.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-rose-500 hover:text-rose-700 h-8 w-8"
                    onClick={() => remove(rule)}
                    data-testid={`auto-approve-rule-delete-${rule.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
